import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import css from './LandingPage.module.css';

//Static assets
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';

//Section pages
import SectionHero from './SectionHero/SectionHero';
import ExploreSection from './Sections/ExploreSection';
import routeConfiguration from '../../routing/routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import SectionRecentlyAdded from './Sections/SectionRecentlyAdded';
import SectionBanners from './Sections/SectionBanners';
import SectionShopByBrands from './Sections/SectionShopByBrands';
import { SHOPIFY_INSTALL_PARAM, shopifyInstallPath } from '../../util/shopify';

export const LandingPageComponent = props => {
  const {
    intl,
    scrollingDisabled,
    history,
    recentlySoldListings,
    recentlyListedListings,
    viewport,
    location,
  } = props;

  const config = useConfiguration();
  const marketplaceName = config.marketplaceName;

  const schemaTitle = intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage({
    id: 'LandingPage.schemaDescription',
  });
  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;

  const useHistoryPush = (page, params = {}) =>
    history.push(createResourceLocatorString(page, routeConfiguration(), {}, params));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const installPath = shopifyInstallPath(params.get(SHOPIFY_INSTALL_PARAM));
    if (installPath) {
      history.replace(installPath);
    }
  }, [history, location.search]);

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <SectionHero viewport={viewport} intl={intl} useHistoryPush={useHistoryPush} />
          <SectionBanners viewport={viewport} intl={intl} useHistoryPush={useHistoryPush} />
          <ExploreSection intl={intl} listings={recentlySoldListings} />
          <SectionShopByBrands viewport={viewport} intl={intl} useHistoryPush={useHistoryPush} />
          <SectionRecentlyAdded intl={intl} listings={recentlyListedListings} />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { recentlySoldListings, recentlyListedListings } = state.LandingPage;

  return {
    recentlySoldListings,
    recentlyListedListings,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({});

const LandingPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport,
  injectIntl
)(LandingPageComponent);

export default LandingPage;
